import { Injectable } from '@angular/core';
import { BooleanStorageKeys, StorageService, StorageType } from './storage.service';

@Injectable({
    providedIn: 'root',
})
export class WebStorageService implements StorageService {
    private storage: Storage = localStorage; // Default to localStorage

    async setString(key: string, value: string): Promise<void> {
        this.storage.setItem(key, value);
    }

    async getString(key: string): Promise<{ value: string | null }> {
        const value = this.storage.getItem(key);
        return Promise.resolve({ value: value ?? null });
    }

    /**
     * Sets a boolean value in the storage using the specified key.
     *
     * @param {BooleanStorageKeys} key - The key associated with the value to be stored.
     * @param {boolean} value - The boolean value to store.
     * @return {Promise<void>} A promise that resolves when the operation is complete.
     */
    async setBoolean(key: BooleanStorageKeys, value: boolean): Promise<void> {
        this.storage.setItem(key, value.toString());
    }

    /**
     *  Retrieves a boolean value from the storage using the specified key.
     * @param key The key associated with the boolean value to retrieve.
     * @param defaultValue The default value to return if the key does not exist.
     * @returns A promise that resolves to the boolean value retrieved from session storage,
     * then local storage if not found in session storage, or the default value if the key does not exist in either storage.
     */
    async getBoolean(key: BooleanStorageKeys, defaultValue?: boolean): Promise<boolean> {
        let value = sessionStorage.getItem(key);
        if (value === null) {
            value = localStorage.getItem(key);
            if (value === null) {
                return defaultValue ?? false;
            }
        }
        return value === 'true';
    }

    async setObject<T>(key: string, value: T): Promise<void> {
        this.storage.setItem(key, JSON.stringify(value));
    }

    /**
     *  Retrieves an object from storage by its key. It first checks sessionStorage, and if the key is not found, it checks localStorage.
     * @param key The key associated with the object to retrieve.
     * @returns A promise that resolves to the object retrieved from session storage, or local storage if not found in session storage,
     *  or null if the key does not exist in either storage.
     */
    async getObject<T>(key: string): Promise<T | null> {
        let value = sessionStorage.getItem(key);
        if (value === null) {
            value = localStorage.getItem(key);
        }
        return Promise.resolve<T | null>(value ? JSON.parse(value) : null);
    }

    async removeItem(key: string): Promise<void> {
        this.storage.removeItem(key);
    }

    async clear(): Promise<void> {
        this.storage.clear();
    }

    setStorageType(type: StorageType): void {
        this.storage = type === StorageType.SESSION ? sessionStorage : localStorage;
    }
}
